// SettingsConnectedPlatforms — manage social connections per brand.
//
// Shows a brand selector at the top. Switching brands reloads the connection
// list so each brand's connections are independently visible and manageable.
//
// Active providers (connectable today): Instagram, Facebook, LinkedIn, Bluesky.
//   - Instagram / Facebook / LinkedIn use the standard OAuth redirect flow.
//   - Bluesky uses the AT Protocol App Password flow (credential form modal).
// Coming-soon providers are displayed but disabled with a "Coming Soon" badge.

import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  api,
  ApiError,
  Brand,
  SocialConnection,
  SocialProvider,
} from "../../lib/api";
import { SocialIcon } from "../../components/SocialIcon";

// ── Active provider metadata ──────────────────────────────────────────────────

const PROVIDER_META: Record<
  SocialProvider,
  { label: string; description: string; authFlow: "oauth" | "credentials" }
> = {
  meta_instagram: {
    label: "Instagram",
    description:
      "Publish posts and stories via Instagram Content Publishing API",
    authFlow: "oauth",
  },
  meta_facebook: {
    label: "Facebook",
    description: "Post to your Facebook Page via the Graph API",
    authFlow: "oauth",
  },
  linkedin: {
    label: "LinkedIn",
    description: "Share updates and articles on LinkedIn",
    authFlow: "oauth",
  },
  bluesky: {
    label: "Bluesky",
    description: "Post to the Bluesky network via the AT Protocol",
    authFlow: "credentials",
  },
  x: {
    label: "X (Twitter)",
    description: "Post tweets via the X API",
    authFlow: "oauth",
  },
};

// Providers that are live and connectable right now.
// X and Bluesky are not enabled yet — listed under "Coming Soon" below.
const ACTIVE_PROVIDERS: SocialProvider[] = [
  "meta_instagram",
  "meta_facebook",
  "linkedin",
];

// ── Coming-soon platform metadata ─────────────────────────────────────────────

interface ComingSoonPlatform {
  provider: string;
  label: string;
  description: string;
  reason: string;
}

const COMING_SOON_PLATFORMS: ComingSoonPlatform[] = [
  {
    provider: "x",
    label: "X (Twitter)",
    description: "Post tweets via the X API",
    reason: "Requires a paid API plan — enabling once we hit revenue goals",
  },
  {
    provider: "bluesky",
    label: "Bluesky",
    description: "Post to the Bluesky network via the AT Protocol",
    reason: "Integration in progress",
  },
  {
    provider: "threads",
    label: "Threads",
    description: "Share updates on Meta's Threads platform",
    reason: "Integration in progress",
  },
  {
    provider: "mastodon",
    label: "Mastodon",
    description: "Publish to Mastodon instances via the ActivityPub API",
    reason: "Integration in progress",
  },
  {
    provider: "tiktok",
    label: "TikTok",
    description: "Share video content directly to TikTok",
    reason: "Integration in progress",
  },
  {
    provider: "pinterest",
    label: "Pinterest",
    description: "Pin visual content to your Pinterest boards",
    reason: "Integration in progress",
  },
  {
    provider: "truth_social",
    label: "Truth Social",
    description: "Publish posts to Truth Social",
    reason: "Integration in progress",
  },
];

// ── Bluesky credential modal ──────────────────────────────────────────────────

interface BlueskyModalProps {
  brandId: string;
  onSuccess: (displayName: string) => void;
  onCancel: () => void;
}

function BlueskyConnectModal({
  brandId,
  onSuccess,
  onCancel,
}: BlueskyModalProps) {
  const [handle, setHandle] = useState("");
  const [appPassword, setAppPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { displayName } = await api.social.connectBluesky(
        handle.trim(),
        appPassword.trim(),
        brandId,
      );
      onSuccess(displayName);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setError(
            "Invalid handle or App Password. Double-check your credentials and try again.",
          );
        } else if (err.status === 503) {
          setError(
            "Bluesky connection is not configured on the server. Contact your administrator.",
          );
        } else {
          setError(err.message || "Connection failed. Please try again.");
        }
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // Backdrop
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <SocialIcon provider="bluesky" size={36} />
          <div>
            <h2 className="section-title">Connect Bluesky</h2>
            <p className="text-xs text-muted">
              Uses an App Password — your main password is never shared
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Handle */}
          <div>
            <label className="label">Bluesky Handle</label>
            <input
              type="text"
              className="input w-full"
              placeholder="yourname.bsky.social"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              autoComplete="username"
              required
              disabled={submitting}
            />
          </div>

          {/* App Password */}
          <div>
            <label className="label">App Password</label>
            <input
              type="password"
              className="input w-full"
              placeholder="xxxx-xxxx-xxxx-xxxx"
              value={appPassword}
              onChange={(e) => setAppPassword(e.target.value)}
              autoComplete="current-password"
              required
              disabled={submitting}
            />
            <p className="mt-1.5 text-xs text-muted">
              Create one at{" "}
              <a
                href="https://bsky.app/settings/app-passwords"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-600 hover:underline"
              >
                bsky.app → Settings → App Passwords
              </a>
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="text-sm px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !handle.trim() || !appPassword.trim()}
              className="btn-primary"
            >
              {submitting ? "Connecting…" : "Connect"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function SettingsConnectedPlatforms() {
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Brand selector state ──────────────────────────────────────────────────
  const [brands, setBrands] = useState<Brand[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(true);

  // Active brandId — prefer URL param, fall back to first brand once loaded
  const [activeBrandId, setActiveBrandId] = useState<string>(
    searchParams.get("brandId") ?? "",
  );

  // ── Connections state ─────────────────────────────────────────────────────
  const [connections, setConnections] = useState<SocialConnection[]>([]);
  const [loadedBrandId, setLoadedBrandId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // ── Bluesky modal state ───────────────────────────────────────────────────
  const [showBlueskyModal, setShowBlueskyModal] = useState(false);

  // ── Publish capability (from the current plan) ────────────────────────────
  // Default true to avoid flashing the upgrade card before plans load.
  const [canPublish, setCanPublish] = useState(true);

  useEffect(() => {
    api.billing
      .plans()
      .then((r) => {
        const def = r.plans.find((p) => p.id === r.current.plan);
        setCanPublish(def?.capabilities.publish ?? false);
      })
      .catch(() => {
        /* non-fatal — leave connect enabled */
      });
  }, []);

  // Derived: true whenever the active brand's connections haven't been fetched yet
  const connectionsLoading = !!activeBrandId && loadedBrandId !== activeBrandId;

  // ── Load brands once ──────────────────────────────────────────────────────
  useEffect(() => {
    api.brands
      .list()
      .then(({ brands: b }) => {
        setBrands(b);
        if (!activeBrandId && b.length > 0) {
          setActiveBrandId(b[0].brandId);
        }
      })
      .catch(() => setError("Failed to load brands"))
      .finally(() => setBrandsLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load connections whenever activeBrandId changes ───────────────────────
  useEffect(() => {
    if (!activeBrandId) return;
    const brandId = activeBrandId;
    api.social
      .list(brandId)
      .then(({ connections: c }) => {
        setError(null);
        setSuccess(null);
        setConnections(c);
        setLoadedBrandId(brandId);
      })
      .catch(() => setError("Failed to load connected platforms"));
  }, [activeBrandId]);

  // ── Handle OAuth callback redirect back to this page ──────────────────────
  useEffect(() => {
    const connected = searchParams.get("connected") as SocialProvider | null;
    const brandFromParam = searchParams.get("brandId");
    if (connected && brandFromParam) {
      setSearchParams({}, { replace: true });
      api.social
        .list(brandFromParam)
        .then(({ connections: c }) => {
          setActiveBrandId(brandFromParam);
          setConnections(c);
          setLoadedBrandId(brandFromParam);
          setSuccess(
            `${PROVIDER_META[connected as SocialProvider]?.label ?? connected} connected successfully.`,
          );
        })
        .catch(console.error);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBrandChange = (brandId: string) => {
    setActiveBrandId(brandId);
    setLoadedBrandId(null);
    setConnections([]);
    setError(null);
    setSuccess(null);
  };

  const handleConnectPlatform = async (provider: SocialProvider) => {
    setError(null);
    const meta = PROVIDER_META[provider];

    // Bluesky uses a credential form instead of an OAuth redirect
    if (meta.authFlow === "credentials") {
      setShowBlueskyModal(true);
      return;
    }

    const label = meta.label;
    try {
      const returnPath = `/settings/platforms`;
      const { authUrl } = await api.social.startOAuth(
        provider,
        activeBrandId,
        returnPath,
      );
      window.location.href = authUrl;
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setError(
          `${label} isn't configured yet — API credentials are missing. Contact your administrator to set up the ${label} integration.`,
        );
      } else {
        setError(
          `Could not connect to ${label}. Please try again or contact support if the problem persists.`,
        );
      }
    }
  };

  const handleDisconnect = async (provider: SocialProvider) => {
    setError(null);
    setSuccess(null);
    const label = PROVIDER_META[provider].label;
    try {
      await api.social.disconnect(provider, activeBrandId);
      setConnections((prev) => prev.filter((c) => c.provider !== provider));
      setSuccess(`${label} disconnected.`);
    } catch {
      setError(`Failed to disconnect ${label}. Please try again.`);
    }
  };

  const handleBlueskySuccess = (displayName: string) => {
    setShowBlueskyModal(false);
    // Re-fetch connections to reflect the new entry
    api.social.list(activeBrandId).then(({ connections: c }) => {
      setConnections(c);
      setLoadedBrandId(activeBrandId);
      setSuccess(`Bluesky connected — ${displayName}`);
    });
  };

  const activeBrand = brands.find((b) => b.brandId === activeBrandId);

  return (
    <div className="max-w-3xl">
      {/* Bluesky credential modal */}
      {showBlueskyModal && (
        <BlueskyConnectModal
          brandId={activeBrandId}
          onSuccess={handleBlueskySuccess}
          onCancel={() => setShowBlueskyModal(false)}
        />
      )}

      <div className="mb-6">
        <h1 className="page-title">Connected Platforms</h1>
        <p className="text-sm text-muted mt-1">
          Each brand has its own social media connections. Select a brand below
          to view or manage its linked accounts.
        </p>
      </div>

      {/* ── Brand selector ── */}
      {!brandsLoading && brands.length > 1 && (
        <div className="mb-6">
          <label className="label mb-1">Brand</label>
          <select
            className="input max-w-xs"
            value={activeBrandId}
            onChange={(e) => handleBrandChange(e.target.value)}
          >
            {brands.map((b) => (
              <option key={b.brandId} value={b.brandId}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* ── Active brand label (single-brand accounts) ── */}
      {!brandsLoading && brands.length === 1 && activeBrand && (
        <div className="mb-5 flex items-center gap-2">
          <span className="text-xs text-muted">Connections for</span>
          <span className="text-xs font-semibold text-ink bg-gray-100 px-2 py-0.5 rounded-full">
            {activeBrand.name}
          </span>
        </div>
      )}

      {/* ── Feedback banners ── */}
      {error && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
          {success}
        </div>
      )}

      {/* ── No brands state ── */}
      {!brandsLoading && brands.length === 0 && (
        <div className="text-sm text-muted py-8 text-center">
          No brands yet. Create a brand first to manage its social connections.
        </div>
      )}

      {/* ── Upgrade gate — connecting/publishing is a paid capability ── */}
      {activeBrandId && !canPublish && (
        <div className="card p-6">
          <p className="eyebrow mb-1">Publishing is a paid feature</p>
          <h2 className="section-title">Upgrade to connect &amp; publish</h2>
          <p className="text-sm text-slate mt-2 max-w-xl">
            On Free you can generate and review posts. Connecting social
            accounts, scheduling, and auto-publishing are available on{" "}
            <strong>Solo</strong> and <strong>Studio</strong>.
          </p>
          <Link to="/billing" className="btn-primary mt-4 inline-flex">
            View plans →
          </Link>
        </div>
      )}

      {/* ── Active connections list ── */}
      {activeBrandId && canPublish && (
        <>
          {connectionsLoading ? (
            <div className="text-sm text-muted">Loading…</div>
          ) : (
            <div className="space-y-3">
              {ACTIVE_PROVIDERS.map((provider) => {
                const meta = PROVIDER_META[provider];
                const conn = connections.find((c) => c.provider === provider);
                return (
                  <div
                    key={provider}
                    className="card p-5 flex items-center justify-between gap-4"
                  >
                    <div className="flex items-center gap-4">
                      <SocialIcon provider={provider} size={40} />
                      <div>
                        <p className="text-sm font-medium text-ink">
                          {meta.label}
                          {conn && (
                            <span className="ml-2 text-xs text-green-600 font-normal">
                              Connected
                              {conn.displayName ? ` — ${conn.displayName}` : ""}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-muted mt-0.5">
                          {meta.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      {conn ? (
                        <button
                          onClick={() => handleDisconnect(provider)}
                          className="text-xs text-red-600 hover:text-red-700 font-medium px-3 py-1.5 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                        >
                          Disconnect
                        </button>
                      ) : (
                        <button
                          onClick={() => handleConnectPlatform(provider)}
                          className="text-xs font-medium px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Connect
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Hint when nothing is connected */}
          {!connectionsLoading && connections.length === 0 && (
            <p className="mt-4 text-xs text-amber-700 bg-amber-50 rounded-lg px-4 py-3">
              No accounts connected for{" "}
              <strong>{activeBrand?.name ?? "this brand"}</strong>. Connect at
              least one platform to enable direct publishing.
            </p>
          )}
        </>
      )}

      {/* ── Coming Soon section ── */}
      <div className="mt-8">
        <h2 className="section-title mb-3">Coming Soon</h2>
        <div className="space-y-3">
          {COMING_SOON_PLATFORMS.map((platform) => (
            <div
              key={platform.provider}
              className="card p-5 flex items-center justify-between gap-4 opacity-60"
            >
              <div className="flex items-center gap-4">
                <SocialIcon provider={platform.provider} size={40} />
                <div>
                  <p className="text-sm font-medium text-ink">
                    {platform.label}
                  </p>
                  <p className="text-xs text-muted mt-0.5">
                    {platform.description}
                  </p>
                  <p className="text-xs text-faint mt-0.5 italic">
                    {platform.reason}
                  </p>
                </div>
              </div>
              <div className="flex-shrink-0">
                <span className="text-xs font-medium px-3 py-1.5 border border-gray-200 rounded-lg bg-gray-50 text-faint cursor-not-allowed select-none">
                  Coming Soon
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
